import type { Group } from "../types/auth";
import { apiClient } from "./apiClient";

// Interfaces para tipado
interface CreateGroupDto {
  nombre: string;
  descripcion: string;
  activo?: boolean;
}

interface UpdateGroupDto {
  nombre?: string;
  descripcion?: string;
  activo?: boolean;
}

/**
 * Servicio para gestión de grupos
 * Utiliza apiClient con interceptores automáticos para autenticación
 * Todas las requests incluyen automáticamente el token JWT
 */
class GroupService {
  /**
   * Obtener todos los grupos
   */
  async getAllGroups(): Promise<Group[]> {
    try {
      const response = await apiClient.get<{
        groups: Group[];
        total: number;
        page: number;
        limit: number;
      }>("/groups");
      return response.data.groups;
    } catch (error) {
      console.error("Error obteniendo grupos:", error);
      throw new Error("No se pudieron cargar los grupos");
    }
  }

  /**
   * Obtener grupos activos únicamente
   */
  async getActiveGroups(): Promise<Group[]> {
    try {
      const response = await apiClient.get<{
        groups: Group[];
        total: number;
        page: number;
        limit: number;
      }>("/groups?activo=true");
      return response.data.groups;
    } catch (error) {
      console.error("Error obteniendo grupos activos:", error);
      throw new Error("No se pudieron cargar los grupos activos");
    }
  }

  /**
   * Obtener grupo por ID
   */
  async getGroupById(id: string): Promise<Group> {
    try {
      const response = await apiClient.get<Group>(`/groups/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error obteniendo grupo:", error);
      throw new Error("No se pudo cargar el grupo");
    }
  }

  /**
   * Crear nuevo grupo
   */
  async createGroup(groupData: CreateGroupDto): Promise<Group> {
    try {
      const response = await apiClient.post<Group>("/groups", groupData);
      return response.data;
    } catch (error) {
      console.error("Error creando grupo:", error);
      throw new Error("No se pudo crear el grupo");
    }
  }

  /**
   * Actualizar grupo existente
   */
  async updateGroup(id: string, groupData: UpdateGroupDto): Promise<Group> {
    try {
      const response = await apiClient.patch<Group>(`/groups/${id}`, groupData);
      return response.data;
    } catch (error) {
      console.error("Error actualizando grupo:", error);
      throw new Error("No se pudo actualizar el grupo");
    }
  }

  /**
   * Eliminar grupo
   */
  async deleteGroup(id: string): Promise<void> {
    try {
      await apiClient.delete(`/groups/${id}`);
    } catch (error) {
      console.error("Error eliminando grupo:", error);
      throw new Error("No se pudo eliminar el grupo");
    }
  }

  /**
   * Activar/Desactivar grupo
   */
  async toggleGroupStatus(id: string, activo: boolean): Promise<Group> {
    try {
      const response = await apiClient.patch<Group>(`/groups/${id}/status`, {
        activo,
      });
      return response.data;
    } catch (error) {
      console.error("Error cambiando estado del grupo:", error);
      throw new Error("No se pudo cambiar el estado del grupo");
    }
  }

  /**
   * Obtener grupos de un usuario específico
   */
  async getUserGroups(userId: string): Promise<Group[]> {
    try {
      const response = await apiClient.get<Group[]>(`/users/${userId}/groups`);
      return response.data;
    } catch (error) {
      console.error("Error obteniendo grupos del usuario:", error);
      throw new Error("No se pudieron cargar los grupos del usuario");
    }
  }

  /**
   * Obtener usuarios de un grupo específico
   */
  async getGroupUsers(groupId: string): Promise<any[]> {
    try {
      const response = await apiClient.get<any[]>(`/groups/${groupId}/users`);
      return response.data;
    } catch (error) {
      console.error("Error obteniendo usuarios del grupo:", error);
      throw new Error("No se pudieron cargar los usuarios del grupo");
    }
  }

  /**
   * Asignar grupos a un usuario
   */
  async assignGroupsToUser(userId: string, groupIds: string[]): Promise<void> {
    try {
      await apiClient.patch(`/users/${userId}/groups`, { groupIds });
    } catch (error) {
      console.error("Error asignando grupos al usuario:", error);
      throw new Error("No se pudieron asignar los grupos al usuario");
    }
  }
}

// Exportar instancia única del servicio
export const groupService = new GroupService();
export type { CreateGroupDto, UpdateGroupDto };
